import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useSearch } from '@tanstack/react-router';
import { ScheduleRequestReviewStatus } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import dayjs from 'dayjs';
import { findExistingTimePeriodKey, useAnalyticsOrgLocations, useScrollToTop } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { theme } from '@frontend/theme';
import { Heading } from '@frontend/design-system';
import { AppointmentAnalyticsBreadcrumb } from '../../atoms/appointment-analytics-breadcrumb';
import { useAppointmentAnalyticsV2ShallowStore } from '../../hooks/use-appointment-analytics-v2-store';
import { useIsAppointmentDemoAccount } from '../../hooks/use-is-appointment-demo-account';
import { AppointmentLocationFilter } from '../filters/appointment-location-filter';
import { ScheduleRequestStatusFilter } from '../filters/schedule-request-status-filter';
import {
  getScheduleRequestTimePeriods,
  ScheduleRequestTimePeriodFilter,
  ScheduleRequestTimePeriods,
} from '../filters/schedule-request-time-period-filter';
import { ScheduleRequestsTable } from '../schedule-requests/schedule-requests-table';

export const ScheduleRequestsView = () => {
  const { t } = useTranslation('analytics');
  const { filters: searchFilters } = useSearch({ from: '/analytics/appointment' });
  const isDemoAccount = useIsAppointmentDemoAccount();
  const { preselectedAccessibleLocationIds } = useAnalyticsOrgLocations({
    isDemoAccount,
    module: 'APPOINTMENT',
  });
  const { activeTab, filters } = useAppointmentAnalyticsV2ShallowStore('activeTab', 'filters');
  useScrollToTop();

  const [localLocationIds, setLocalLocationIds] = useState<string[] | undefined>();
  const [localDates, setLocalDates] = useState<{ startDate?: string; endDate?: string }>(() => {
    const fallbackStart = searchFilters?.startDate ?? filters[activeTab]?.startDate;
    const fallbackEnd = searchFilters?.endDate ?? filters[activeTab]?.endDate;
    if (fallbackStart && fallbackEnd) {
      return { startDate: fallbackStart, endDate: fallbackEnd };
    }
    const firstPeriod = Object.values(getScheduleRequestTimePeriods(t))[0];
    return {
      startDate: dayjs(firstPeriod.value.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
      endDate: dayjs(firstPeriod.value.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS'),
    };
  });
  const [localStatus, setLocalStatus] = useState<ScheduleRequestReviewStatus[]>(
    searchFilters?.status ? [searchFilters.status as ScheduleRequestReviewStatus] : []
  );

  useEffect(() => {
    if (preselectedAccessibleLocationIds?.length && localLocationIds === undefined) {
      setLocalLocationIds(preselectedAccessibleLocationIds);
    }
  }, [preselectedAccessibleLocationIds, localLocationIds]);

  const timePeriods = useMemo(() => getScheduleRequestTimePeriods(t), [t]);

  const subtitle = useMemo(() => {
    if (!localDates.startDate || !localDates.endDate) return undefined;

    const periodKey = findExistingTimePeriodKey<ScheduleRequestTimePeriods>(
      localDates.startDate,
      localDates.endDate,
      timePeriods
    );

    if (periodKey) {
      return t('Showing results for {{period}}.', { period: timePeriods[periodKey].label });
    }

    const startDate = dayjs(localDates.startDate);
    const endDate = dayjs(localDates.endDate);

    if (startDate.isSame(endDate, 'day')) {
      return t('Showing results for {{date}}.', { date: startDate.format('MMM D, YYYY') });
    }

    return t('Showing results for {{startDate}} - {{endDate}}.', {
      startDate: startDate.format('MMM D, YYYY'),
      endDate: endDate.format('MMM D, YYYY'),
    });
  }, [t, localDates, timePeriods]);

  return (
    <Page>
      <Page.Header>
        <AppointmentAnalyticsBreadcrumb />
        <Page.Header.Heading>
          <Page.Header.Title
            title={
              <Heading level={1} css={{ display: 'inline-flex', alignItems: 'baseline', gap: theme.spacing(1) }}>
                {t('Schedule Request History')}
              </Heading>
            }
          />
          {subtitle && <Page.Header.Subtitle subtitle={subtitle} />}
        </Page.Header.Heading>
      </Page.Header>
      <Page.Body>
        <div css={styles.bodyContent}>
          <div css={styles.filtersWrapper}>
            <ScheduleRequestTimePeriodFilter
              startDate={localDates.startDate}
              endDate={localDates.endDate}
              onChange={(dates) => setLocalDates(dates)}
            />
            <AppointmentLocationFilter value={localLocationIds} onChange={setLocalLocationIds} />
            <ScheduleRequestStatusFilter value={localStatus} onChange={setLocalStatus} />
          </div>
          <ScheduleRequestsTable
            locationId={localLocationIds}
            startDate={localDates.startDate}
            endDate={localDates.endDate}
            reviewedStatus={localStatus}
          />
        </div>
      </Page.Body>
    </Page>
  );
};

const styles = {
  bodyContent: css`
    padding-top: ${theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
  filtersWrapper: css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;
  `,
};
